import React, {useEffect, useState} from "react";
import {GridColDef, GridPaginationModel} from '@mui/x-data-grid-pro';
import {useLazyQuery, useMutation} from "@apollo/client";
import _ from "lodash";
import moment from "moment";
import {paginationLimit} from "../../config/paginationLimit";
import Table from "../Table";
import {useTranslation} from "react-i18next";
import {ToastMessageList} from "../../graphql/queries/toastMessageList";
import {DeleteToastMessage} from "../../graphql/mutations/deleteToastMessage";
import getFilter from "../../utils/getFilter";
import {ActionsMenu, getFilterParams, getInitialPagination} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {DeleteRounded, EditRounded} from "@mui-symbols-material/w300";
import {toast} from "react-toastify";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import {ToastMessageStatus} from "../../codegen/graphql";
import {Chip} from "@mui/joy";
import {Tooltip} from "@mui/material";
import {getIotStatusChipColors, IOT_STATUS_COLOR_MAP} from "../../config/iotConnectionStatusConfig";
import {isToastMessageCurrentlyDisplayed} from "../../utils/toastMessageVisibility";

interface Props {
    height: number,
    onChangePagination?: (newPaginationModel: GridPaginationModel) => void,
    urlParams: URLSearchParams,
    filterKeys?: string[],
    onEditSystemAnnouncement: (toastMessageId: string) => void;
}

const SystemAnnouncementsTable = ({
                                      height,
                                      onChangePagination,
                                      urlParams,
                                      filterKeys,
                                      onEditSystemAnnouncement,
                                  }: Props) => {
    const {t} = useTranslation();

    const [paginationModel, setPaginationModel] = useState(getInitialPagination(paginationLimit, urlParams));
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const filter = getFilterParams(filterKeys, urlParams);
    const filterString = JSON.stringify(filter);

    useEffect(() => {
        setPaginationModel({
            page: getInitialPagination(paginationLimit, urlParams).page,
            pageSize: getInitialPagination(paginationLimit, urlParams).pageSize
        })
    }, [filterString])

    useEffect(() => {
        if (onChangePagination) {
            onChangePagination(paginationModel)
        }
    }, [paginationModel])

    const [deleteToastMessageMutation, deleteToastMessageResult] = useMutation(DeleteToastMessage)
    const [fetchToastMessageList, toastMessageListQuery] = useLazyQuery(ToastMessageList, {
        variables: {
            pagination: {
                start: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize
            },
            filter: {
                status: getFilter(filter.status, "equals"),
                // single datetime filter inputs: announcements starting after
                // "Valid From" and ending before "Valid To"
                fromDate: getFilter(filter.fromDate, "after"),
                toDate: getFilter(filter.toDate, "before"),
            }
        },
        fetchPolicy: "network-only"
    });
    const toastMessages = _.get(toastMessageListQuery, 'data.toastMessageList.toastMessages', []);
    const totalToastMessages = _.get(toastMessageListQuery, 'data.toastMessageList.total');

    useEffect(() => {
        fetchToastMessageList();
    }, [fetchToastMessageList]);

    const onDeleteToastMessage = () => {
        if (!deleteId) return;
        deleteToastMessageMutation({
            variables: {
                id: deleteId,
            },
            refetchQueries: ["ToastMessageList", "ActiveToastMessages"]
        })
            .then((res) => {
                if (res.data?.deleteToastMessage?.success) {
                    toast.success(res.data?.deleteToastMessage?.message || t('Successfully deleted system announcement'));
                }
                setDeleteId(null);
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t))
                setDeleteId(null);
            })
    }

    const columns: GridColDef[] = [
        {
            field: 'status',
            headerName: t('Status'),
            width: 120,
            display: 'flex',
            align: 'center',
            renderCell: (params) => {
                const isActive = params.row.status === ToastMessageStatus.ACTIVE;
                // Same badge look as the IoT asset status chips: Active ↔ Running, Inactive ↔ Off
                const colors = getIotStatusChipColors(isActive ? 'RUNNING' : 'OFF');
                if (isActive && !isToastMessageCurrentlyDisplayed(params.row)) {
                    // Active, but the current date is outside fromDate..toDate →
                    // no user sees it right now: outlined instead of filled.
                    return (
                        <Tooltip
                            arrow
                            title={t('This announcement is active, but it is currently not shown to users because the current date is outside its date range.')}
                        >
                            <Chip
                                variant="outlined"
                                sx={{
                                    color: colors.text,
                                    '--variant-borderWidth': '1px',
                                    borderColor: IOT_STATUS_COLOR_MAP['RUNNING'],
                                }}
                            >
                                {t('Active')}
                            </Chip>
                        </Tooltip>
                    );
                }
                return (
                    <Chip sx={{backgroundColor: colors.bg, color: colors.text}}>
                        {isActive ? t('Active') : t('Inactive')}
                    </Chip>
                );
            },
            disableColumnMenu: true
        },
        {
            field: 'message',
            headerName: t('Message'),
            flex: 1,
            disableColumnMenu: true
        },
        {
            field: 'fromDate',
            headerName: t('From'),
            width: 160,
            valueGetter: (value) => {
                return value ? moment(value).format('DD.MM.YYYY HH:mm') : '-';
            },
            disableColumnMenu: true
        },
        {
            field: 'toDate',
            headerName: t('To'),
            width: 160,
            valueGetter: (value) => {
                return value ? moment(value).format('DD.MM.YYYY HH:mm') : '-';
            },
            disableColumnMenu: true
        },
        {
            field: 'actions',
            headerName: t('Actions'),
            width: 80,
            disableColumnMenu: true,
            align: "center",
            renderCell: (params) => {
                const toastMessageId = _.get(params, 'row.id')

                const actionItems: { item: React.ReactNode, onClick: () => void, active: boolean }[] = [
                    {
                        item: <><EditRounded fontSize={'inherit'} sx={{mr: '4px'}}/>{t('Edit')}</>,
                        onClick: () => onEditSystemAnnouncement(toastMessageId),
                        active: true,
                    },
                    {
                        item: <><DeleteRounded fontSize={'inherit'} sx={{mr: '4px'}}/>{t('Delete')}</>,
                        onClick: () => setDeleteId(toastMessageId),
                        active: true,
                    },
                ]

                return <ActionsMenu items={actionItems}/>
            }
        }
    ]

    return (
        <div style={{width: "100%", height: height}}>
            <Table
                loading={toastMessageListQuery.loading}
                data={toastMessages}
                rowCount={totalToastMessages}
                columns={columns}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                pageSizeOptions={[paginationLimit]}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSorting
                disableRowSelectionOnClick
                disableColumnResize
                disableColumnReorder
                disableColumnPinning
            />
            {deleteId && (
                <ConfirmDeleteModal
                    open={!!deleteId}
                    title={t('Delete system announcement')}
                    message={t('Are you sure you want to delete this system announcement? It will no longer be shown to any user.')}
                    loading={deleteToastMessageResult.loading}
                    onConfirm={onDeleteToastMessage}
                    onClose={() => setDeleteId(null)}
                />
            )}
        </div>
    );
}

export default SystemAnnouncementsTable;
